import React, { useState, useContext } from 'react';
import { ApolloCache, ApolloError } from '@apollo/client';
import { useServerData } from '@xingternal/hops';
import { trackEntityPageFollowAction } from '@xingternal/pages-tracking'; // eslint-disable-line
import { ErrorContext } from '@xingternal/pages-page-error';
import {
  redirectToLogin,
  useLoggedinAction,
  ENTITY_PAGE_QUERY,
  ENTITY_SUBPAGE_QUERY,
  usePageContext,
  getEntityPageQueryVariables,
  getEntitySubpageQueryVariables,
} from '@xingternal/pages-common';
import type {
  EntityPageQuery,
  EntitySubpageQuery,
} from '@xingternal/pages-common';

import Follow from './Follow';
import { useEntityPageFollowMutation } from './EntityPageFollowMutation.gql-types';
import { useEntityPageUnfollowMutation } from './EntityPageUnfollowMutation.gql-types';

import FollowActionConfirmation from './FollowActionConfirmation';

type FollowContainerProps = {
  pageId?: string;
  isFollowingPage?: boolean;
  onCompleted?: () => void;
  className?: string;
};

const FollowContainer = ({
  pageId: followedId,
  isFollowingPage,
  onCompleted = () => {},
  ...props
}: FollowContainerProps) => {
  const { authenticated = false } = useServerData();
  const { showError } = useContext(ErrorContext);
  const { pageContext } = usePageContext();
  const [followed, setFollowed] = useState(isFollowingPage);
  const [
    showFollowConfirmationDialog,
    setShowFollowConfirmationDialog,
  ] = useState(false);

  const handleCache = (cache: ApolloCache<any>, follow: boolean) => {
    const dataMainPage: EntityPageQuery | null = cache.readQuery({
      query: ENTITY_PAGE_QUERY,
      variables: getEntityPageQueryVariables(pageContext.pageSlug),
    });

    if (
      dataMainPage?.entityPageEX?.__typename === 'EntityPage' &&
      dataMainPage?.entityPageEX?.modules?.collection
    ) {
      const [header, ...rest] = dataMainPage.entityPageEX.modules.collection;
      const followers =
        header?.__typename === 'EntityPageHeaderModule' &&
        header?.content &&
        'followers' in header?.content &&
        header.content.followers;
      const newModulesCollection = [
        {
          ...header,
          content: {
            ...(header?.__typename === 'EntityPageHeaderModule' &&
              header.content),
            followers: {
              ...followers,
              total: (followers ? followers?.total : 0) + (follow ? 1 : -1),
            },
          },
        },
        ...rest,
      ];

      cache.writeQuery({
        query: ENTITY_PAGE_QUERY,
        variables: getEntityPageQueryVariables(pageContext.pageSlug),
        data: {
          entityPageEX: {
            ...dataMainPage.entityPageEX,
            modules: {
              collection: newModulesCollection,
              __typename: 'EntityPageModules',
            },
            userPageContext: {
              ...dataMainPage.entityPageEX.userPageContext,
              userInteractions: {
                followState: {
                  isFollowing: follow,
                  __typename: 'FollowContext',
                },
                __typename: 'EntityPageUserInteractionFollow',
              },
            },
          },
        },
      });
    }

    const dataSubpage: EntitySubpageQuery | null = cache.readQuery({
      query: ENTITY_SUBPAGE_QUERY,
      variables: getEntitySubpageQueryVariables(pageContext.pageSlug),
    });

    dataSubpage?.entityPageEX &&
      cache.writeQuery({
        query: ENTITY_SUBPAGE_QUERY,
        variables: getEntitySubpageQueryVariables(pageContext.pageSlug),
        data: {
          entityPageEX: {
            ...dataSubpage.entityPageEX,
            userPageContext: {
              ...(dataSubpage.entityPageEX.__typename === 'EntityPage' &&
                dataSubpage.entityPageEX.userPageContext),
              userInteractions: {
                followState: {
                  isFollowing: follow,
                  __typename: 'FollowContext',
                },
                __typename: 'EntityPageUserInteractionFollow',
              },
            },
          },
        },
      });
  };

  const [
    followPage,
    { loading: followPageLoading },
  ] = useEntityPageFollowMutation({
    onCompleted: () => trackEntityPageFollowAction({ hasFollowed: true }),
    onError: (error) => toggleFollow(error),
    update: (cache) => handleCache(cache, true),
  });

  const [
    unfollowPage,
    { loading: unfollowPageLoading },
  ] = useEntityPageUnfollowMutation({
    onCompleted: () => trackEntityPageFollowAction({ hasFollowed: false }),
    onError: (error) => toggleFollow(error),
    update: (cache) => handleCache(cache, false),
  });

  const toggleFollow = (error?: ApolloError) => {
    if (!authenticated) {
      redirectToLogin('followEntityPage');
    } else {
      setFollowed(!followed);
      if (error) {
        trackEntityPageFollowAction({ hasFollowed: followed, error: true });
        showError({
          message: 'EP_JOBS_FEEDBACK_ERROR',
          error: error.message,
        });
      } else {
        followedId && (
          followed
          ? unfollowPage({ variables: { followedId } })
          : followPage({ variables: { followedId } })
        );
      }
    }
  };

  const toggleFollowWithDialog = () => {
    setTimeout(() => setShowFollowConfirmationDialog(true), 1000);
  };

  useLoggedinAction(toggleFollow, 'followEntityPage', followed);
  useLoggedinAction(toggleFollowWithDialog, 'followConfirm', false);

  return (
    <>
      <FollowActionConfirmation
        show={showFollowConfirmationDialog}
        setShow={setShowFollowConfirmationDialog}
        onConfirm={toggleFollow}
      />
      <Follow
        loading={Boolean(followPageLoading || unfollowPageLoading)}
        toggleFollow={toggleFollow}
        isFollowing={followed}
        {...props}
      />
    </>
  );
};

export default FollowContainer;
